/*
 * Carrinho lateral Bracci (snippets/cart-drawer.liquid + bracci-cart-*).
 *
 * 1. Yampi: o script da Yampi (snippets/YampiSnippet.liquid) só troca o botão de
 *    checkout na página /cart. No carrinho lateral, o clique em "Finalizar compra"
 *    iria para o checkout nativo do Shopify; aqui ele é desviado para a Yampi
 *    (yampiClick), como no tema antigo (assets/cart-drawer.js).
 * 2. Voltando do checkout da Yampi, a página vem do cache com o carrinho antigo:
 *    recarrega para mostrar o estado real.
 * 3. "Continuar comprando" sem link fecha o carrinho.
 * 4. Pré-venda: limita a quantidade da linha ao que resta do limite (data-max).
 * 5. "Combina com o seu carrinho" (<bracci-cart-upsell>): recomendações do Shopify
 *    para o último item, sem repetir o que já está no carrinho; sem recomendações,
 *    usa a coleção de reserva. O "Adicionar" é o do cartão de produto (bracci-card.js).
 */
(() => {
  if (window.BracciCart) return;

  /* ---------- 1 e 2. Yampi ---------- */

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('#cart-drawer [name="checkout"], #cart-drawer #checkout') : null;
      if (!button) return;
      if (typeof window.yampiClick !== 'function') return; // Yampi fora do ar: segue o checkout normal
      if (typeof window.currentPage !== 'undefined' && window.currentPage === 'cart') return; // lá a Yampi já cuida

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // O carrinho é esvaziado pela página de compra concluída da Yampi. Se o cliente
      // voltou depois de comprar, mandar carrinho vazio deixaria o clique morto.
      fetch('/cart.json', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => response.json())
        .then((cart) => {
          if (!cart || cart.item_count === 0) {
            window.location.reload();
            return;
          }
          window.yampiClick();
        })
        .catch(() => window.yampiClick());
    },
    true
  );

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    fetch('/cart.json', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => response.json())
      .then((cart) => {
        const shown = document.querySelectorAll('#cart-drawer .cart-items__table-row').length;
        if (cart.item_count === 0 && shown > 1) window.location.reload();
      })
      .catch(() => {});
  });

  /* ---------- 3. Continuar comprando ---------- */

  document.addEventListener('click', (event) => {
    const close = event.target instanceof Element ? event.target.closest('[data-bcart-close]') : null;
    if (!close) return;
    const drawer = document.getElementById('cart-drawer');
    if (typeof drawer?.close === 'function') drawer.close();
    else drawer?.querySelector('.theme-drawer__close-button')?.click();
  });

  /* ---------- 4. Limite da pré-venda ---------- */

  function applyLimits(root = document) {
    root.querySelectorAll('[data-bcart-meta][data-max]').forEach((meta) => {
      const row = meta.closest('tr, .cart-items__table-row, li');
      const input = row?.querySelector('input[type="number"], input[name="updates[]"], [ref="quantityInput"]');
      if (input) input.max = meta.dataset.max;
    });
  }

  /* ---------- 5. Sugestões ---------- */

  const cache = new Map();

  function formatMoney(cents, format) {
    if (window.BracciCard?.formatMoney) return window.BracciCard.formatMoney(cents, format);
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
  }

  function sized(url, width) {
    if (!url) return '';
    try {
      const absolute = url.startsWith('//') ? `https:${url}` : url;
      const parsed = new URL(absolute, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.href;
    } catch (error) {
      return url;
    }
  }

  class BracciCartUpsell extends HTMLElement {
    static get observedAttributes() {
      return ['data-product-id', 'data-exclude'];
    }

    connectedCallback() {
      this.load();
      this.addEventListener('click', (event) => {
        const prev = event.target.closest('[data-bcart-prev]');
        const next = event.target.closest('[data-bcart-next]');
        if (!prev && !next) return;
        const track = this.querySelector('[data-bcart-track]');
        const first = track?.firstElementChild;
        const step = first ? first.getBoundingClientRect().width + 10 : 180;
        track?.scrollBy({ left: next ? step : -step, behavior: 'smooth' });
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== null && oldValue !== newValue) this.load();
    }

    async fetchRecommendations(productId, intent, limit) {
      const key = `${productId}|${intent}|${limit}`;
      if (cache.has(key)) return cache.get(key);

      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}recommendations/products.json?product_id=${productId}&limit=${limit + 6}&intent=${intent}`;
      const request = fetch(url)
        .then((response) => (response.ok ? response.json() : { products: [] }))
        .then((data) =>
          (data.products || []).map((product) => {
            const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
            return {
              id: product.id,
              title: product.title,
              url: product.url,
              price: product.price,
              image: product.featured_image,
              variant: variant?.id,
              variants: product.variants?.length || 1,
              available: product.available,
            };
          })
        )
        .catch(() => []);
      cache.set(key, request);
      return request;
    }

    async load() {
      const track = this.querySelector('[data-bcart-track]');
      if (!track) return;

      const productId = this.dataset.productId;
      const limit = Number(this.dataset.limit) || 6;
      const exclude = new Set(String(this.dataset.exclude || '').split(',').filter(Boolean));
      const token = Symbol('load');
      this.loadToken = token;

      let list = productId ? await this.fetchRecommendations(productId, this.dataset.intent || 'related', limit) : [];
      if (this.loadToken !== token) return;

      list = list.filter((product) => product.available !== false && !exclude.has(String(product.id)));

      if (list.length === 0) {
        const fallback = this.querySelector('[data-bcart-fallback]');
        if (fallback) {
          try {
            list = JSON.parse(fallback.textContent).filter((product) => !exclude.has(String(product.id)));
          } catch (error) {
            list = [];
          }
        }
      }

      list = list.slice(0, limit);
      this.hidden = list.length === 0;
      if (!list.length) return;

      const pix = Number(this.dataset.pix) || 0;
      const format = this.dataset.moneyFormat;
      const label = this.dataset.button || 'Adicionar';

      track.innerHTML = list
        .map((product) => {
          const price = Number(product.price) || 0;
          const shown = pix > 0 ? Math.round((price * (100 - pix)) / 100) : price;
          const image = product.image
            ? `<img src="${escapeHtml(sized(product.image, 240))}" alt="" width="120" height="120" loading="lazy">`
            : '';
          const action =
            Number(product.variants) > 1 || !product.variant
              ? `<a class="bcart-upc__button" href="${escapeHtml(product.url)}">Ver opções</a>`
              : `<div class="bcart-upc__add" data-bc-add data-label-add="${escapeHtml(label)}" data-label-added="Adicionado ✓" data-label-soldout="${escapeHtml(this.dataset.soldout || 'Esgotado')}" data-label-error="Não foi possível adicionar">
                   <button type="button" class="bc-add__button bcart-upc__button" data-bc-add-button data-variant-id="${escapeHtml(product.variant)}">
                     <span data-bc-add-label>${escapeHtml(label)}</span>
                   </button>
                 </div>`;
          return `<div class="bcart-upc">
              <a class="bcart-upc__image" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">${image}</a>
              <div class="bcart-upc__info">
                <a class="bcart-upc__title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
                <span class="bcart-upc__price">${formatMoney(shown, format)}${pix > 0 ? ' <em>à vista</em>' : ''}</span>
                ${action}
              </div>
            </div>`;
        })
        .join('');

      const nav = this.querySelector('.bcart-upsell__nav');
      if (nav) nav.hidden = list.length < 2;
    }
  }

  if (!customElements.get('bracci-cart-upsell')) customElements.define('bracci-cart-upsell', BracciCartUpsell);

  /* ---------- Reaplicar depois que o Fabric redesenha o carrinho ---------- */

  let timer;
  function refresh() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      applyLimits();
      document.querySelectorAll('bracci-cart-upsell').forEach((upsell) => {
        const track = upsell.querySelector('[data-bcart-track]');
        if (track && !track.children.length) upsell.load();
      });
    }, 60);
  }

  function observe() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    new MutationObserver(refresh).observe(drawer, { childList: true, subtree: true });
    refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observe);
  else observe();

  window.BracciCart = { applyLimits, refresh };
})();
